import React from 'react';
import { Loader2, CheckCircle2, FileText, Brain, Database, Search, AlertCircle, X, RefreshCw } from 'lucide-react';

interface MissionProgressProps {
    status?: string | null;
    title?: string;
    isDone?: boolean;       // parent signals "this upload just finished"
    onDismiss: () => void;  // always required — only parent can dismiss
    canDismiss?: boolean;   // if false, hide the X (upload still in-flight)
}

// ─── Strict status → visual mapping ────────────────────────────────────────
const getProgressInfo = (status: string | null | undefined) => {
    const s = (status || '').toLowerCase().trim();
    switch (s) {
        // ── Edge function statuses ──────────────────────────────────
        case 'received':
            return { pct: 10, text: "Réception du document…" };
        case 'extraction_pdf':
            return { pct: 30, text: "Extraction et lecture du contenu…" };
        case 'analyse_ia':
            return { pct: 60, text: "Analyse approfondie par l'IA…" };
        case 'generation_ads':
            return { pct: 85, text: "Génération des vecteurs d'embedding…" };
        // ── legacy statuses ─────────────────────────────────────────
        case 'reçu':
        case 'recu':
        case 'initialisation':
            return { pct: 10, text: "Réception du document…" };
        case 'analyse type de document':
        case 'analyse type':
        case 'analyse_type':
            return { pct: 30, text: "Extraction et lecture du contenu…" };
        case 'analyse des agents':
        case 'analyse agents':
        case 'analyse_agents':
            return { pct: 60, text: "Analyse approfondie par l'IA…" };
        case 'analyse complete':
        case 'analyse complète':
        case 'analyse_complete':
            return { pct: 85, text: "Génération des vecteurs d'embedding…" };
        // ── Terminal statuses ───────────────────────────────────────
        case 'prospection':
            return { pct: 100, text: "Analyse terminée avec succès." };
        case 'mission existante':
        case 'mission_existante':
            return { pct: 100, text: "Doublon détecté — mission déjà analysée." };
        default:
            return { pct: 10, text: "Réception du document…" };
    }
};

// ─── Unified 5-step thresholds (aligned with AnalysisProgressWidget) ────────
const STEP_THRESHOLDS = [
    { label: 'Réception', pct: 10, Icon: FileText },
    { label: 'Extraction', pct: 30, Icon: Search },
    { label: 'Analyse IA', pct: 60, Icon: Brain },
    { label: 'Vectorisation', pct: 85, Icon: Database },
    { label: 'Terminé', pct: 100, Icon: CheckCircle2 },
];

const MissionProgressWidget: React.FC<MissionProgressProps> = ({
    status,
    title = 'Nouvelle Mission',
    onDismiss,
    canDismiss = true,
}) => {
    const info = getProgressInfo(status);
    const isExisting = (status || '').toLowerCase().includes('mission existante') || (status || '').toLowerCase().includes('mission_existante');
    const isDone = info.pct === 100;

    return (
        <div className={`rounded-xl p-4 border shadow-sm transition-all duration-300 mb-2 animate-in fade-in slide-in-from-top-2
            ${isDone ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'}
        `}>
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                        ${isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-50 text-indigo-600'}
                    `}>
                        {isDone
                            ? <CheckCircle2 size={20} />
                            : <Loader2 size={20} className="animate-spin" />
                        }
                    </div>

                    {/* Text */}
                    <div>
                        <h4 className="font-semibold text-slate-800 text-sm truncate max-w-xs">{title}</h4>
                        <p className={`text-xs ${isDone ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                            {info.text}
                        </p>
                    </div>
                </div>

                {/* Right-side actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Actualiser button — safety net when done and not a duplicate */}
                    {isDone && !isExisting && (
                        <button
                            onClick={() => window.location.reload()}
                            className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-md transition-colors"
                        >
                            <RefreshCw size={12} />
                            Actualiser
                        </button>
                    )}

                    {/* Always dismissable */}
                    {canDismiss && (
                        <button
                            onClick={onDismiss}
                            title="Fermer"
                            className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                        >
                            <X size={16} />
                        </button>
                    )}

                    {/* Lock icon when upload is still in-flight — visual cue only */}
                    {!canDismiss && (
                        <span title="Analyse en cours, impossible de fermer" className="text-slate-300 p-1">
                            <AlertCircle size={16} />
                        </span>
                    )}
                </div>
            </div>

            {/* Progress bar */}
            <div className="relative">
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full transition-all duration-700 ease-out
                            ${isDone ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                        style={{ width: `${info.pct}%` }}
                    />
                </div>

                {/* Step labels */}
                <div className="flex justify-between mt-2 px-0.5">
                    {STEP_THRESHOLDS.map(({ label, pct, Icon }) => (
                        <div
                            key={label}
                            className={`flex flex-col items-center gap-0.5 transition-colors duration-300
                                ${info.pct >= pct ? 'text-indigo-600' : 'text-slate-300'}
                                ${isDone ? '!text-emerald-500' : ''}
                            `}
                        >
                            <Icon size={11} />
                            <span className="text-[10px] font-medium">{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MissionProgressWidget;
